import { yupResolver } from '@hookform/resolvers/yup'
import { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  TOAST_SUCCESS_DEFAULT_OPTIONS,
  TOAST_ERROR_DEFAULT_OPTIONS,
} from '../../../../../../../../constants/toast'
import {
  type UpdateExpertParams,
  useExpertMutation,
} from '../../../../../../../../services/hooks/experts/mutations/useExpertMutation'
import { getErrorMessage } from '../../../../../../../../utils/getErrorMessage'
import { normalizeCpf } from '../../../../../../../../utils/normalizeCpf'
import { bankDataSchema } from '../../../../../../../../validators/bankDataSchema'
import { naturalPersonSchema } from '../../../../../../../../validators/naturalPersonSchema'
import { type FormsStepsValues } from './types/FormsSteps'
import { type ExpertDomain } from '../../../../../../../../types/domain/expert-domain'

export const useNaturalStepperContainer = (expert?: ExpertDomain) => {
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState<0 | 1>(0)

  const schema = useMemo(() => {
    if (currentStep === 0) {
      return naturalPersonSchema
    }

    return bankDataSchema
  }, [currentStep])

  const { formatMessage } = useIntl()
  const { mutate, isLoading: isExpertSubmitting } = useExpertMutation(
    expert!.id
  )

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    resetOptions: {
      keepDirtyValues: true,
    },
    defaultValues: {
      cpf: expert?.cpf,
      name: expert?.name,
      email: expert?.email,
      phone: expert?.phone,
      linkedin: expert?.linkedin,
      addressNumber: expert?.naturalData?.addressNumber,
      birthdate: expert?.naturalData?.birthdate,
      rg: expert?.naturalData?.rg,
      cep: expert?.naturalData?.cep,
      street: expert?.naturalData?.street,
      complement: expert?.naturalData?.complement || '',
      neighborhood: expert?.naturalData?.neighborhood,
      city: expert?.naturalData?.city,
      state: expert?.naturalData?.state,
      bank: expert?.bankData?.bank,
      currentAccount: expert?.bankData?.currentAccount,
      bankBranch: expert?.bankData?.bankBranch,
    },
  })

  const handleCancel = () => navigate('/expert/personal-data')

  const handleSubmit = (data: FormsStepsValues) => {
    const formattedData: UpdateExpertParams = {
      personType: data.personType,
      cpf: normalizeCpf(data.cpf),
      name: data.name,
      phone: data.phone,
      email: data.email,
      linkedin: data.linkedin ?? undefined,
      naturalData: {
        addressNumber: data.addressNumber,
        birthdate: data.birthdate,
        rg: data.rg,
        cep: data.cep,
        street: data.street,
        complement: data.complement || '',
        neighborhood: data.neighborhood,
        city: data.city,
        state: data.state,
      },
      bankData: {
        bank: data.bank || '',
        currentAccount: data.currentAccount || '',
        bankBranch: data?.bankBranch || '',
        pix: data?.pix || '',
      },
    }

    mutate(
      { id: expert!.id, data: formattedData },
      {
        onSuccess() {
          navigate('/expert/personal-data')
          toast.success(
            formatMessage({ id: 'DATA_SUCCESSFULLY_SAVED' }),
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )
        },
        onError(error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  return {
    form,
    currentStep,
    isExpertSubmitting,
    setCurrentStep,
    handleCancel,
    handleSubmit,
  }
}
